import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useMutation, useQueryClient, useQuery, useQueries } from '@tanstack/react-query';
import TaskHistorySection from './TaskHistorySection';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select } from './ui/select';
import { useToast } from './ui/use-toast';
import api, { streamAIResponse, tasksAPI } from '../lib/api';
import { getStatusColor, formatDate } from '../lib/utils';
import { 
  X, MapPin, Calendar, User, MessageSquare, Image as ImageIcon, 
  AlertCircle, FileText, Building2, Loader2, ChevronLeft, ChevronRight, 
  Upload, Wrench, Camera, CheckCircle2, Pause, Play, XCircle,
  Maximize2, Download, File, Archive, ChevronDown
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator
} from './ui/dropdown-menu';
import DOMPurify from 'dompurify';

// Helper to get status label
const getStatusLabel = (estado) => {
  if (!estado) return 'Sin Estado';
  const estadoLower = estado.toLowerCase();
  if (estadoLower.includes('finalizada') || estadoLower.includes('completada')) return 'Completada';
  if (estadoLower.includes('ejecucion') || estadoLower.includes('progreso')) return 'En Progreso';
  if (estadoLower.includes('reparacion') && !estadoLower.includes('finalizada')) return 'En Reparación';
  if (estadoLower.includes('pendiente')) return 'Pendiente';
  if (estadoLower.includes('rechazada')) return 'Rechazada';
  if (estadoLower.includes('bloqueada')) return 'Bloqueada';
  return estado;
};

// Convert file to base64
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      // Remove the data:image/xxx;base64, prefix
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
  });
};

export default function TaskDetails({ task, onClose, onUpdate }) {
  const [comment, setComment] = useState('');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadPreviews, setUploadPreviews] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch task images
  const { data: imagesData, isLoading: imagesLoading, error: imagesError, refetch: refetchImages } = useQuery({
    queryKey: ['task-images', task.id],
    queryFn: async () => {
      const response = await api.get(`/tasks/${task.id}/images`);
      return response.data;
    },
    enabled: true,
    retry: 1,
  });

  // Fetch task documents (tipo D)
  const { data: documentsData, isLoading: documentsLoading } = useQuery({
    queryKey: ['task-documents', task.id],
    queryFn: async () => {
      const response = await api.get(`/tasks/${task.id}/documents`);
      return response.data;
    },
    retry: 1,
  });

  // Memoize images array to prevent recreation on every render
  const images = React.useMemo(() => imagesData?.images || [], [imagesData]);
  const documents = React.useMemo(() => documentsData?.documents || [], [documentsData]);
  const hasImagePermissionError = imagesError?.response?.status === 403 || imagesData?.error?.includes('403');

  // Pre-fetch ALL images in background so carousel/lightbox navigation is instant
  useQueries({
    queries: images.slice(0, 15).map(img => ({
      queryKey: ['image-data', img.id, task.id],
      queryFn: async () => {
        const r = await api.get(`/tasks/${task.id}/images/${img.id}/download`);
        const url = r.data?.dataUrl;
        if (!url?.startsWith('data:image/')) throw new Error('invalid');
        return url;
      },
      enabled: images.length > 1 && !!img.id,
      staleTime: 30 * 60 * 1000,
      retry: 1,
    }))
  });
  
  // Fetch task comments  
  const { data: commentsData, isLoading: commentsLoading } = useQuery({
    queryKey: ['task-comments', task.id],
    queryFn: async () => {
      const response = await api.get(`/tasks/${task.id}/comments`);
      return response.data;
    },
    enabled: true,
  });

  // Memoize comments array to prevent recreation on every render
  const comments = React.useMemo(() => commentsData?.comments || [], [commentsData]);

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Status change dialog state
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [statusComment, setStatusComment] = useState('');
  const [isChangingStatus, setIsChangingStatus] = useState(false);

  // AI response state
  const [aiResponse, setAiResponse] = useState('');
  const [showAiResponse, setShowAiResponse] = useState(false);
  const [aiStreaming, setAiStreaming] = useState(false);
  const aiAbortRef = React.useRef(null);

  // Use React Query for image loading — one query per active image index
  // This avoids setState-in-effect pattern
  const currentImageMeta = images[currentImageIndex];
  const lightboxImageMeta = images[lightboxIndex];

  const { data: currentImageUrl, isFetching: currentImageLoading } = useQuery({
    queryKey: ['image-data', currentImageMeta?.id, task.id],
    queryFn: async () => {
      const r = await api.get(`/tasks/${task.id}/images/${currentImageMeta.id}/download`);
      const url = r.data?.dataUrl;
      if (!url?.startsWith('data:image/')) throw new Error('invalid');
      return url;
    },
    enabled: !!currentImageMeta?.id,
    staleTime: 30 * 60 * 1000,
    retry: 1,
  });

  const { data: lightboxImageUrl, isFetching: lightboxImageLoading } = useQuery({
    queryKey: ['image-data', lightboxImageMeta?.id, task.id],
    queryFn: async () => {
      const r = await api.get(`/tasks/${task.id}/images/${lightboxImageMeta.id}/download`);
      const url = r.data?.dataUrl;
      if (!url?.startsWith('data:image/')) throw new Error('invalid');
      return url;
    },
    enabled: lightboxOpen && !!lightboxImageMeta?.id,
    staleTime: 30 * 60 * 1000,
    retry: 1,
  });

  // Keep legacy imageDataUrls map for download button compatibility
  const imageDataUrls = React.useMemo(() => {
    const map = {};
    if (currentImageMeta?.id && currentImageUrl) map[currentImageMeta.id] = currentImageUrl;
    if (lightboxImageMeta?.id && lightboxImageUrl) map[lightboxImageMeta.id] = lightboxImageUrl;
    return map;
  }, [currentImageMeta, currentImageUrl, lightboxImageMeta, lightboxImageUrl]);

  const openLightbox = useCallback((index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  }, []);

  const closeLightbox = useCallback(() => setLightboxOpen(false), []);

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!lightboxOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') setLightboxIndex(prev => (prev + 1) % images.length);
      if (e.key === 'ArrowLeft') setLightboxIndex(prev => (prev - 1 + images.length) % images.length);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightboxOpen, images.length, closeLightbox]);

  // Pre-fetch image when lightbox navigates — handled by useQuery above

  // Estado change dialog state - initialized above
  // Available statuses (aligned with SCMejora API IDs)
  const availableStatuses = [
    { id: 14, name: 'Pendiente de ejecución', icon: AlertCircle, color: 'text-yellow-600', requiresComment: false },
    { id: 20, name: 'Pausada', icon: Pause, color: 'text-orange-600', requiresComment: true },
    { id: 17, name: 'En ejecución', icon: Play, color: 'text-blue-600', requiresComment: false },
    { id: 18, name: 'Finalizada', icon: CheckCircle2, color: 'text-green-600', requiresComment: false },
    { id: 19, name: 'Rechazada', icon: XCircle, color: 'text-red-600', requiresComment: true },
  ];

  // Change status mutation
  const changeStatusMutation = useMutation({
    mutationFn: async ({ taskId, statusId, comment }) => {
      const payload = { estado_id: statusId };
      if (comment) payload.comentario = comment;
      return api.put(`/tasks/${taskId}/status`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['stats'] });
      toast.success('Estado actualizado correctamente');
      setShowStatusDialog(false);
      setStatusComment('');
      setSelectedStatus(null);
    },
    onError: (error) => {
      console.error('Error changing status:', error);
      toast.error(error.response?.data?.detail || 'Error al cambiar el estado');
    }
  });

  const handleStatusChange = async () => {
    if (!selectedStatus) return;
    
    // Validate comment if required
    if (selectedStatus.requiresComment && !statusComment.trim()) {
      toast.error('El comentario es obligatorio para este estado');
      return;
    }

    setIsChangingStatus(true);
    try {
      await changeStatusMutation.mutateAsync({
        taskId: task.id,
        statusId: selectedStatus.id,
        comment: statusComment.trim() || undefined
      });
    } finally {
      setIsChangingStatus(false);
    }
  };

  // AI state initialized above
  const handleGenerateAiResponse = () => {
    setAiResponse('');
    setShowAiResponse(true);
    setAiStreaming(true);
    let accumulated = '';
    aiAbortRef.current = streamAIResponse(
      task.id,
      (chunk) => { accumulated += chunk; setAiResponse(accumulated); },
      () => setAiStreaming(false),
      (err) => {
        setAiStreaming(false);
        toast({ title: 'Error IA', description: err.message, variant: 'destructive' });
      }
    );
  };

  const handleCopyAiResponse = () => {
    navigator.clipboard.writeText(aiResponse);
    toast({ title: 'Copiado', description: 'Respuesta copiada al portapapeles' });
  };

  const handleUseAiAsComment = () => {
    setComment(aiResponse);
    setShowAiResponse(false);
    toast({ title: 'Respuesta añadida', description: 'Puedes editarla antes de enviar' });
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      const maxSize = 10 * 1024 * 1024; // 10MB
      
      if (!validTypes.includes(file.type)) {
        toast({ 
          title: 'Formato no válido', 
          description: `${file.name} no es un formato de imagen válido`,
          variant: 'destructive' 
        });
        return false;
      }
      if (file.size > maxSize) {
        toast({ 
          title: 'Archivo muy grande', 
          description: `${file.name} supera el límite de 10MB`,
          variant: 'destructive' 
        });
        return false;
      }
      return true;
    });

    // Create previews
    const newPreviews = validFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name
    }));

    setSelectedFiles(prev => [...prev, ...validFiles]);
    setUploadPreviews(prev => [...prev, ...newPreviews]);
  };

  // Remove selected file
  const removeSelectedFile = (index) => {
    URL.revokeObjectURL(uploadPreviews[index].preview);
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setUploadPreviews(prev => prev.filter((_, i) => i !== index));
  };

  // Upload images
  const handleUploadImages = async () => {
    if (selectedFiles.length === 0) return;

    setIsUploading(true);
    let successCount = 0;
    let errorCount = 0;

    for (const file of selectedFiles) {
      try {
        const base64 = await fileToBase64(file);
        const extension = file.name.split('.').pop().toLowerCase();
        
        await api.post(`/tasks/${task.id}/images`, {
          nombre: file.name,
          extension: extension,
          base64: base64,
          tipo: 'I',
          momento_subida: 'antes-finalizacion'
        });
        
        successCount++;
      } catch (error) {
        console.error('Error uploading image:', error);
        errorCount++;
      }
    }

    setIsUploading(false);

    // Clear selections
    uploadPreviews.forEach(p => URL.revokeObjectURL(p.preview));
    setSelectedFiles([]);
    setUploadPreviews([]);

    // Show result
    if (successCount > 0) {
      toast({ 
        title: 'Imágenes subidas', 
        description: `${successCount} imagen(es) subida(s) correctamente${errorCount > 0 ? `, ${errorCount} fallida(s)` : ''}` 
      });
      refetchImages();
    } else {
      toast({ 
        title: 'Error', 
        description: 'No se pudieron subir las imágenes',
        variant: 'destructive' 
      });
    }
  };

  // Helper: convert any SCMejora field that might be an object to a displayable string
  // SCMejora can return user/entity fields as objects: {id, codigo, nombre, apellidos, ...}
  const safeText = (val) => {
    if (val === null || val === undefined || val === '') return '';
    if (typeof val === 'object') {
      // Person object: {nombre, apellidos, ...}
      const parts = [val.nombre, val.apellidos].filter(Boolean);
      if (parts.length > 0) return parts.join(' ').trim();
      // Generic: try descripcion, then codigo, then first string value
      return val.descripcion || val.nombre || val.codigo || val.id || '';
    }
    return String(val);
  };

  // Get description - use contenido (detailed description) if available
  const rawDescription = task.contenido || task.contenido_original;
  const fullDescription = typeof rawDescription === 'object' ? safeText(rawDescription) : (rawDescription || '');
  const shortDescription = safeText(task.descripcion || task.titulo);
  const tipoIncidencia = safeText(task.nombre_uni_ges || task.tipo_incidencia);
  const encargado = safeText(task.DesEncargadoCompleto || task.encargado);
  const entidadReparadora = safeText(task.DesEntidadReparadora || task.entidad_reparadora);
  
  // Map priority codes to descriptive names
  const getPriorityLabel = (priority) => {
    if (priority === null || priority === undefined) return 'No especificada';
    
    // Handle SCMejora object format: {codigo: "alta", nombre: "alta", color: "#..."}
    if (typeof priority === 'object') {
      const nombre = priority.nombre || priority.codigo || '';
      const nameMap = {
        'muy_alta': 'Muy Alta', 'urgente': 'Urgente', 'critica': 'Crítica',
        'alta': 'Alta',
        'media': 'Media',
        'baja': 'Baja',
        'muy_baja': 'Muy Baja'
      };
      return nameMap[nombre.toLowerCase()] || nombre || 'No especificada';
    }
    
    const priorityNum = parseInt(priority);
    const priorityMap = { 5: 'Urgente', 4: 'Alta', 3: 'Media', 2: 'Baja', 1: 'Muy Baja' };
    return priorityMap[priorityNum] || 'No especificada';
  };

  // SECURITY FIX: Use DOMPurify to prevent XSS attacks (was using dangerous innerHTML)
  const sanitizeHTML = useCallback((html) => {
    if (!html) return '';
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'p', 'br', 'ul', 'ol', 'li', 'span', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
      ALLOWED_ATTR: ['href', 'target', 'class'],
      FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'button'],
      FORBID_ATTR: ['onclick', 'onerror', 'onload']
    });
  }, []);

  // Priority mutation — manual override, independent from SCMejora
  const priorityMutation = useMutation({
    mutationFn: (prioridad_manual) => tasksAPI.updateTaskPriority(task.id, prioridad_manual),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      toast({ title: `Prioridad actualizada a "${res.data.nombre}"`, description: 'Guardado localmente. No se modifica en SCMejora.' });
    },
    onError: () => toast({ title: 'Error al actualizar prioridad', variant: 'destructive' }),
  });

  const priorityOptions = [
    { value: 5, label: 'Urgente', color: 'bg-red-500' },
    { value: 4, label: 'Alta', color: 'bg-orange-500' },
    { value: 3, label: 'Media', color: 'bg-yellow-500' },
    { value: 2, label: 'Baja', color: 'bg-green-500' },
    { value: 1, label: 'Muy Baja', color: 'bg-blue-400' },
  ];

  const getPriorityBadgeColor = (num) => {
    if (num >= 5) return 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-400 border-red-200 dark:border-red-700';
    if (num === 4) return 'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-400 border-orange-200 dark:border-orange-700';
    if (num === 3) return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-400 border-yellow-200 dark:border-yellow-700';
    if (num === 2) return 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-400 border-green-200 dark:border-green-700';
    return 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-400 border-blue-200 dark:border-blue-700';
  };

  const effectivePriority = task.prioridad_manual || task.prioridad_num;

  return (
    <>
    <div className="w-96 bg-white dark:bg-gray-900 border-l border-border overflow-y-auto">
      <Card className="rounded-none border-0">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-lg">Detalles de Incidencia</CardTitle>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="close-details">
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {/* Task Code */}
          <div>
            <span className="text-xs font-mono text-muted-foreground bg-muted px-2 py-1 rounded">{task.codigo}</span>
          </div>

          {/* Title - Short description */}
          <div>
            <h3 className="font-semibold text-lg">{shortDescription || 'Sin título'}</h3>
          </div>

          {/* Status & Priority */}
          <div className="flex items-center space-x-2 flex-wrap gap-1">
            <Badge className={getStatusColor(task.estado_reparacion || task.estado || task.DesEstadoObra)}>
              {getStatusLabel(task.estado_reparacion || task.estado || task.DesEstadoObra)}
            </Badge>
            {/* Interactive priority picker — click to change */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="priority-badge-btn"
                  className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold border cursor-pointer hover:opacity-80 transition-opacity ${getPriorityBadgeColor(effectivePriority)}`}
                >
                  {getPriorityLabel(effectivePriority)}
                  {task.prioridad_manual && <span className="text-[9px] ml-0.5 opacity-60">✎</span>}
                  <ChevronDown className="w-2.5 h-2.5 ml-0.5 opacity-60" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-44">
                <DropdownMenuLabel className="text-xs text-muted-foreground">
                  Cambiar prioridad
                  {task.prioridad_manual && <span className="ml-1 text-primary font-medium">(manual)</span>}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {priorityOptions.map(opt => (
                  <DropdownMenuItem
                    key={opt.value}
                    onClick={() => priorityMutation.mutate(opt.value)}
                    className={`gap-2 cursor-pointer ${effectivePriority === opt.value ? 'font-semibold' : ''}`}
                    data-testid={`priority-option-${opt.value}`}
                  >
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${opt.color}`} />
                    {opt.label}
                    {effectivePriority === opt.value && <span className="ml-auto text-primary text-xs">✓</span>}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Type of Incident */}
          {tipoIncidencia && (
            <div className="flex items-start space-x-2">
              <FileText className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" />
              <div>
                <Label className="text-xs text-muted-foreground">Tipo de Incidencia</Label>
                <p className="text-sm">{tipoIncidencia}</p>
              </div>
            </div>
          )}

          {/* Full Description - Contenido */}
          {fullDescription && (
            <div className="bg-muted/30 p-3 rounded-md">
              <Label className="text-xs text-muted-foreground">Descripción Detallada</Label>
              <div 
                className="text-sm mt-1 prose prose-sm dark:prose-invert max-w-none"
                dangerouslySetInnerHTML={{ __html: sanitizeHTML(fullDescription) }}
              />
            </div>
          )}

          {/* Additional Information */}
          {(task.origen_nombre || task.emplazamiento || task.barrio) && (
            <div className="bg-muted/30 p-3 rounded-md space-y-2">
              <Label className="text-xs text-muted-foreground font-semibold">Información Adicional</Label>
              
              {task.origen_nombre && (
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Origen:</span>
                  <span className="font-medium">{safeText(task.origen_nombre)}</span>
                </div>
              )}
              
              {task.emplazamiento && task.emplazamiento !== task.direccion && (
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Emplazamiento:</span>
                  <span className="font-medium">{safeText(task.emplazamiento)}</span>
                </div>
              )}
              
              {task.barrio && (
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Barrio:</span>
                  <span className="font-medium">{safeText(task.barrio)}</span>
                </div>
              )}
              
              {task.distrito && (
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Distrito:</span>
                  <span className="font-medium">{safeText(task.distrito)}</span>
                </div>
              )}
            </div>
          )}

          {/* Actions Section */}
          <div className="border-t pt-4 mt-4">
            <Label className="text-xs text-muted-foreground font-semibold mb-3 block">Acciones</Label>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => setShowStatusDialog(true)}
              data-testid="change-status-button"
            >
              <Wrench className="w-4 h-4 mr-2" />
              Cambiar Estado
            </Button>
          </div>

          {/* Location */}
          {task.direccion && (
            <div className="flex items-start space-x-2">
              <MapPin className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" />
              <div>
                <Label className="text-xs text-muted-foreground">Ubicación</Label>
                <p className="text-sm">{safeText(task.direccion)}</p>
                {(task.barrio || task.distrito) && (
                  <p className="text-xs text-muted-foreground">{safeText(task.barrio)} - {safeText(task.distrito)}</p>
                )}
              </div>
            </div>
          )}

          {/* Date */}
          {(task.fecha || task.fecha_incidencia) && (
            <div className="flex items-start space-x-2">
              <Calendar className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" />
              <div>
                <Label className="text-xs text-muted-foreground">Fecha Incidencia</Label>
                <p className="text-sm">{formatDate(task.fecha_incidencia || task.fecha)}</p>
              </div>
            </div>
          )}

          {/* Entidad Reparadora */}
          {entidadReparadora && (
            <div className="flex items-start space-x-2">
              <Building2 className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" />
              <div>
                <Label className="text-xs text-muted-foreground">Entidad Reparadora</Label>
                <p className="text-sm">{entidadReparadora}</p>
              </div>
            </div>
          )}

          {/* Encargado */}
          {encargado && (
            <div className="flex items-start space-x-2">
              <Wrench className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" />
              <div>
                <Label className="text-xs text-muted-foreground">Encargado</Label>
                <p className="text-sm">{encargado}</p>
              </div>
            </div>
          )}

          {/* Reporter */}
          {(task.reportado_por || task.DesUsuario) && (
            <div className="flex items-start space-x-2">
              <User className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" />
              <div>
                <Label className="text-xs text-muted-foreground">Reportado por</Label>
                <p className="text-sm">{safeText(task.reportado_por || task.DesUsuario)}</p>
              </div>
            </div>
          )}

          {/* Images Section */}
          <div className="pt-4 border-t">
            <Label className="text-sm font-semibold mb-2 flex items-center">
              <ImageIcon className="w-4 h-4 mr-1" />
              Imágenes ({images.length})
            </Label>
            
            {imagesLoading ? (
              <div className="flex items-center justify-center py-8 bg-muted/30 rounded-md">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                <span className="ml-2 text-sm text-muted-foreground">Cargando imágenes...</span>
              </div>
            ) : images.length > 0 ? (
              <div className="relative">
                <div className="aspect-video bg-muted rounded-md overflow-hidden flex items-center justify-center">
                  {currentImageLoading ? (
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">Cargando imagen...</span>
                    </div>
                  ) : currentImageUrl ? (
                    <div
                      className="relative w-full h-full group cursor-zoom-in"
                      onClick={() => openLightbox(currentImageIndex)}
                      data-testid="image-thumbnail-clickable"
                    >
                      <img
                        src={currentImageUrl}
                        alt={images[currentImageIndex]?.nombre || `Imagen ${currentImageIndex + 1}`}
                        className="w-full h-full object-contain"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 rounded-full p-2">
                          <Maximize2 className="w-5 h-5 text-white" />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-3 p-4">
                      <AlertCircle className="w-8 h-8 text-amber-500" />
                      <span className="text-xs text-muted-foreground text-center">
                        No se pudo cargar la imagen desde el servidor
                      </span>
                      {images[currentImageIndex]?.id && (
                        <a
                          href={`https://santacruz.sigoincidencias.com/adjuntos/imagen/${images[currentImageIndex].id}/view`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:text-blue-800 underline flex items-center gap-1"
                          data-testid="open-image-scmejora"
                        >
                          <Camera className="w-3 h-3" />
                          Abrir en SCMejora
                        </a>
                      )}
                    </div>
                  )}
                </div>
                {images.length > 1 && (
                  <div className="flex items-center justify-between mt-2">
                    <Button variant="outline" size="sm" onClick={prevImage}>
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-xs text-muted-foreground">
                      {currentImageIndex + 1} / {images.length}
                    </span>
                    <Button variant="outline" size="sm" onClick={nextImage}>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                )}
                <p className="text-xs text-muted-foreground mt-1 text-center">
                  {images[currentImageIndex]?.nombre} - {images[currentImageIndex]?.usuario}
                </p>
              </div>
            ) : (
              <div className="py-4 text-center text-sm text-muted-foreground bg-muted/30 rounded-md">
                {hasImagePermissionError ? (
                  <div className="space-y-2">
                    <AlertCircle className="w-8 h-8 mx-auto text-yellow-500" />
                    <p className="font-medium text-yellow-700 dark:text-yellow-500">
                      Imágenes no disponibles
                    </p>
                    <p className="text-xs">
                      No tienes permisos para ver las imágenes de esta incidencia. 
                      Contacta al administrador del sistema.
                    </p>
                  </div>
                ) : imagesLoading ? (
                  <div className="flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Cargando imágenes...</span>
                  </div>
                ) : (
                  'No hay imágenes adjuntas'
                )}
              </div>
            )}

            {/* Upload Section */}
            <div className="mt-4 space-y-3">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                data-testid="image-file-input"
              />
              
              <Button
                variant="outline"
                className="w-full"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
              >
                <Camera className="w-4 h-4 mr-2" />
                Seleccionar Imágenes
              </Button>

              {/* Preview selected files */}
              {uploadPreviews.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">
                    Imágenes seleccionadas ({uploadPreviews.length})
                  </Label>
                  <div className="grid grid-cols-3 gap-2">
                    {uploadPreviews.map((preview, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={preview.preview}
                          alt={preview.name}
                          className="w-full h-16 object-cover rounded-md border"
                        />
                        <button
                          onClick={() => removeSelectedFile(index)}
                          className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <Button
                    className="w-full"
                    onClick={handleUploadImages}
                    disabled={isUploading}
                    data-testid="upload-images-button"
                  >
                    {isUploading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Subiendo...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Subir {uploadPreviews.length} imagen(es)
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Documents Section */}
          {(documents.length > 0 || documentsLoading) && (
            <div className="pt-4 border-t" data-testid="documents-section">
              <Label className="text-sm font-semibold flex items-center gap-2 mb-3">
                <FileText className="w-4 h-4 text-blue-600" />
                Documentos Adjuntos ({documents.length})
              </Label>
              {documentsLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-4 h-4 animate-spin mr-2 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">Cargando documentos...</span>
                </div>
              ) : (
                <div className="space-y-2">
                  {documents.map((doc) => {
                    const ext = (doc.extension || '').toLowerCase();
                    const isPdf = ext === 'pdf';
                    const isWord = ['doc', 'docx'].includes(ext);
                    const isExcel = ['xls', 'xlsx'].includes(ext);
                    const DocIcon = isPdf ? FileText : isExcel ? FileText : isWord ? FileText : File;
                    const iconColor = isPdf ? 'text-red-500' : isExcel ? 'text-green-600' : isWord ? 'text-blue-600' : 'text-gray-500';
                    const bgColor = isPdf ? 'bg-red-50' : isExcel ? 'bg-green-50' : isWord ? 'bg-blue-50' : 'bg-gray-50';

                    return (
                      <div
                        key={doc.id}
                        data-testid={`document-${doc.id}`}
                        className="flex items-center gap-3 p-2.5 rounded-lg border border-border hover:bg-muted/30 transition-colors group"
                      >
                        <div className={`w-9 h-9 rounded-lg ${bgColor} flex items-center justify-center flex-shrink-0`}>
                          <DocIcon className={`w-5 h-5 ${iconColor}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium truncate">{doc.nombre}</p>
                          <p className="text-[10px] text-muted-foreground mt-0.5">
                            {ext.toUpperCase()}
                            {doc.fecha && ` · ${formatDate(doc.fecha)}`}
                            {doc.usuario && ` · ${doc.usuario}`}
                          </p>
                        </div>
                        <a
                          href="#"
                          data-testid={`document-download-${doc.id}`}
                          onClick={async (e) => {
                            e.preventDefault();
                            try {
                              const res = await api.get(`/tasks/${task.id}/documents/${doc.id}/download`);
                              const dataUrl = res.data?.dataUrl;
                              if (!dataUrl) return;
                              const link = document.createElement('a');
                              link.href = dataUrl;
                              link.download = doc.nombre || `documento_${doc.id}.${doc.extension || 'bin'}`;
                              link.click();
                            } catch (err) {
                              toast({ title: 'Error', description: 'No se pudo descargar el documento', variant: 'destructive' });
                            }
                          }}
                          className="flex-shrink-0 p-1.5 rounded-md opacity-0 group-hover:opacity-100 hover:bg-muted transition-all text-muted-foreground hover:text-foreground"
                          title="Descargar"
                        >
                          <Download className="w-4 h-4" />
                        </a>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Comments Section */}
          <div className="pt-4 border-t">
            <div className="flex items-center justify-between mb-3">
              <Label className="text-sm font-semibold flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Comentarios y Respuestas ({comments.length})
              </Label>
              <Button
                variant="outline"
                size="sm"
                onClick={handleGenerateAiResponse}
                disabled={aiStreaming}
                data-testid="generate-ai-response"
              >
                {aiStreaming ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />Generando...</> : 'Generar Respuesta IA'}
              </Button>
            </div>
            
            {commentsLoading ? (
              <div className="flex items-center justify-center gap-2 py-4 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Cargando comentarios...</span>
              </div>
            ) : comments.length > 0 ? (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {comments.map((comment) => (
                  <div 
                    key={comment.id} 
                    className={`p-3 rounded-md ${
                      comment.propio 
                        ? 'bg-primary/10 border-l-2 border-primary' 
                        : 'bg-muted/30'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <span className="text-xs font-medium text-muted-foreground">
                        {safeText(comment.usuario) || 'Usuario'}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {comment.fecha ? new Date(comment.fecha).toLocaleDateString('es-ES', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : ''}
                      </span>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">
                      {comment.comentario}
                    </p>
                    {comment.propio && (
                      <span className="inline-block mt-2 text-xs text-primary font-medium">
                        Tu comentario
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-4 text-center text-sm text-muted-foreground bg-muted/30 rounded-md">
                No hay comentarios disponibles
              </div>
            )}

            {/* Add New Comment */}
            <div className="mt-4 space-y-2">
              {showAiResponse && (
                <div className="mb-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-md">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-blue-700 dark:text-blue-300 flex items-center gap-1">
                      {aiStreaming && <Loader2 className="w-3 h-3 animate-spin" />}
                      {aiStreaming ? 'Generando respuesta IA...' : 'Respuesta generada por IA:'}
                    </span>
                    <div className="flex space-x-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleCopyAiResponse}
                        className="h-6 text-xs"
                      >
                        Copiar
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleUseAiAsComment}
                        className="h-6 text-xs"
                      >
                        Usar
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowAiResponse(false)}
                        className="h-6 text-xs"
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{aiResponse}</p>
                </div>
              )}
              
              <Label htmlFor="new-comment" className="text-xs">Agregar Comentario</Label>
              <Textarea
                id="new-comment"
                placeholder="Escribe un comentario..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={3}
                className="text-sm"
                data-testid="add-comment-input"
              />
              <Button
                onClick={async () => {
                  if (!comment.trim()) {
                    toast({ title: 'Error', description: 'El comentario no puede estar vacío', variant: 'destructive' });
                    return;
                  }
                  try {
                    await api.post(`/tasks/${task.id}/comments`, { comentario: comment });
                    toast({ title: 'Éxito', description: 'Comentario agregado correctamente' });
                    setComment('');
                    queryClient.invalidateQueries({ queryKey: ['task-comments', task.id] });
                  } catch (error) {
                    toast({ title: 'Error', description: error.response?.data?.detail || 'Error al agregar comentario', variant: 'destructive' });
                  }
                }}
                size="sm"
                className="w-full"
                data-testid="add-comment-button"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Agregar Comentario
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Status Change Dialog */}
      {showStatusDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Wrench className="w-5 h-5 mr-2" />
                Cambiar Estado
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Status selection */}
              <div className="space-y-2">
                <Label>Nuevo Estado</Label>
                <div className="grid gap-2">
                  {availableStatuses.map((status) => {
                    const Icon = status.icon;
                    return (
                      <Button
                        key={status.id}
                        variant={selectedStatus?.id === status.id ? "default" : "outline"}
                        className="w-full justify-start"
                        onClick={() => setSelectedStatus(status)}
                      >
                        <Icon className={`w-4 h-4 mr-2 ${status.color}`} />
                        {status.name}
                      </Button>
                    );
                  })}
                </div>
              </div>

              {/* Comment field */}
              {selectedStatus?.requiresComment && (
                <div className="space-y-2">
                  <Label htmlFor="status-comment">
                    Comentario <span className="text-destructive">*</span>
                  </Label>
                  <Textarea
                    id="status-comment"
                    placeholder="Explica el motivo..."
                    value={statusComment}
                    onChange={(e) => setStatusComment(e.target.value)}
                    rows={3}
                  />
                </div>
              )}

              {/* Action buttons */}
              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowStatusDialog(false);
                    setSelectedStatus(null);
                    setStatusComment('');
                  }}
                  disabled={isChangingStatus}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleStatusChange}
                  disabled={!selectedStatus || isChangingStatus}
                >
                  {isChangingStatus && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Confirmar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      {/* Status History Section — inside the scrollable panel */}
      <TaskHistorySection taskId={task.id} />
    </div>

    {/* ===== LIGHTBOX FULLSCREEN ===== */}
    {lightboxOpen && (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center"
        style={{ backgroundColor: 'rgba(0,0,0,0.93)' }}
        onClick={closeLightbox}
        data-testid="lightbox-overlay"
      >
        {/* Stop propagation on content clicks */}
        <div
          className="relative w-full h-full flex flex-col items-center justify-center"
          onClick={e => e.stopPropagation()}
        >

          {/* Close button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 z-10 bg-white/10 hover:bg-white/25 text-white rounded-full p-2 transition-colors"
            data-testid="lightbox-close"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Download button */}
          {lightboxImageUrl && (
            <a
              href={lightboxImageUrl}
              download={images[lightboxIndex]?.nombre || `imagen_${lightboxIndex + 1}.jpg`}
              className="absolute top-4 right-16 z-10 bg-white/10 hover:bg-white/25 text-white rounded-full p-2 transition-colors"
              data-testid="lightbox-download"
              title="Descargar imagen"
            >
              <Download className="w-5 h-5" />
            </a>
          )}

          {/* Counter + filename */}
          <div className="absolute top-4 left-4 z-10 text-white/80 text-sm select-none">
            <span className="font-semibold">{lightboxIndex + 1} / {images.length}</span>
            {images[lightboxIndex]?.nombre && (
              <span className="ml-2 text-white/60">{images[lightboxIndex].nombre}</span>
            )}
          </div>

          {/* Main image */}
          <div className="flex items-center justify-center w-full h-full px-16 py-16">
            {lightboxImageLoading ? (
              <div className="text-white/50 flex flex-col items-center gap-3">
                <Loader2 className="w-10 h-10 animate-spin" />
                <span className="text-sm">Cargando imagen...</span>
              </div>
            ) : lightboxImageUrl ? (
              <img
                src={lightboxImageUrl}
                alt={images[lightboxIndex]?.nombre || `Imagen ${lightboxIndex + 1}`}
                className="max-w-full max-h-full object-contain rounded-md select-none"
                style={{ maxHeight: 'calc(100vh - 120px)' }}
                data-testid="lightbox-image"
              />
            ) : (
              <div className="text-white/50 flex flex-col items-center gap-3">
                <AlertCircle className="w-10 h-10 text-amber-500" />
                <span className="text-sm">No se pudo cargar la imagen</span>
              </div>
            )}
          </div>

          {/* Prev / Next */}
          {images.length > 1 && (
            <>
              <button
                onClick={() => setLightboxIndex(prev => (prev - 1 + images.length) % images.length)}
                className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/25 text-white rounded-full p-3 transition-colors"
                data-testid="lightbox-prev"
              >
                <ChevronLeft className="w-7 h-7" />
              </button>
              <button
                onClick={() => setLightboxIndex(prev => (prev + 1) % images.length)}
                className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/25 text-white rounded-full p-3 transition-colors"
                data-testid="lightbox-next"
              >
                <ChevronRight className="w-7 h-7" />
              </button>
            </>
          )}

          {/* Bottom info bar */}
          <div className="absolute bottom-4 left-0 right-0 flex items-center justify-center gap-3 text-white/60 text-xs select-none">
            {images[lightboxIndex]?.usuario && (
              <span><User className="w-3 h-3 inline mr-1" />{images[lightboxIndex].usuario}</span>
            )}
            {images[lightboxIndex]?.fecha && (
              <span><Calendar className="w-3 h-3 inline mr-1" />{images[lightboxIndex].fecha}</span>
            )}
            <span className="text-white/30">· Esc para cerrar</span>
          </div>

          {/* Dot navigation */}
          {images.length > 1 && (
            <div className="absolute bottom-10 flex gap-2">
              {images.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setLightboxIndex(i)}
                  className={`w-2 h-2 rounded-full transition-all ${i === lightboxIndex ? 'bg-white scale-125' : 'bg-white/30 hover:bg-white/60'}`}
                  data-testid={`lightbox-dot-${i}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    )}
    </>
  );
}
